import { Fragment, FC, useState } from "react"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import { Userbot } from "../../interfaces"
import { useFinishedChainQuery } from "../../store"
import { getApiErrorMessage } from "@/core/utils"
import { previewJson } from "../common/jsonUtils"
import { formatStepFailure } from "./stepFailure"
import { StepResultDetails } from "./stepResultDetails"
import { ReportProvenance } from "./reportProvenance"

interface IProps {
  id: string
}

const statusColor = (
  status: Userbot.ChainReportStep["status"],
): "success" | "error" | "warning" => {
  if (status === "completed") return "success"
  if (status === "failed") return "error"
  return "warning"
}

const fmt = (v: string | null | undefined): string =>
  v ? new Date(v).toLocaleString("ru") : "—"

/** true, если результат joinChat — отправленная и ещё не одобренная заявка. */
const isPendingJoin = (step: Userbot.ChainReportStep): boolean => {
  if (step.action !== "telegram.joinChat" || !step.result) return false
  const r = step.result as Partial<Userbot.JoinResult>
  return r.requested === true && r.membershipStatus === "pending"
}

/** Детальный отчёт завершённой цепочки (read-only). */
export const FinishedChainReport: FC<IProps> = ({ id }) => {
  const { data, isFetching, isError, error } = useFinishedChainQuery({ id })

  const [expandedStepId, setExpandedStepId] = useState<string | null>(null)
  const toggleExpand = (id: string) =>
    setExpandedStepId((prev) => (prev === id ? null : id))

  if (isFetching && !data) return <CircularProgress />
  if (isError)
    return <Alert severity="error">{getApiErrorMessage(error)}</Alert>
  if (!data) return null

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" mb={1}>
          {data.name}
        </Typography>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Chip color="success" label={`completed: ${data.completed}`} />
          <Chip color="error" label={`failed: ${data.failed}`} />
          <Chip color="warning" label={`skipped: ${data.skipped}`} />
        </Box>
        <Stack spacing={0.5}>
          <ReportProvenance report={data} />
          <Typography variant="body2" color="text.secondary">
            Клиент: {data.clientId}
          </Typography>
          {data.comment && (
            <Typography variant="body2" color="text.secondary">
              Комментарий: {data.comment}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Создана: {fmt(data.createdAt)} · Старт: {fmt(data.startedAt)} ·
            Финиш: {fmt(data.finishedAt)}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" mb={1}>
          Шаги
        </Typography>
        {data.steps.length === 0 ? (
          <Typography color="text.secondary">Шаги отсутствуют.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Шаг</TableCell>
                <TableCell>Действие</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Попыток</TableCell>
                <TableCell>Ошибка</TableCell>
                <TableCell>Результат</TableCell>
                <TableCell>Завершён</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.steps.map((s) => (
                <Fragment key={s.stepId}>
                  <TableRow hover>
                    <TableCell>{s.stepId}</TableCell>
                    <TableCell>{s.action}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(s.status)}
                        label={s.status}
                      />
                    </TableCell>
                    <TableCell>{s.attemptsMade}</TableCell>
                    <TableCell>{formatStepFailure(s)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {isPendingJoin(s) ? (
                          <Chip
                            size="small"
                            color="warning"
                            label="Заявка отправлена, ждём одобрения"
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {previewJson(s.result)}
                          </Typography>
                        )}
                        <IconButton
                          size="small"
                          aria-label={`Детали результата ${s.stepId}`}
                          onClick={() => toggleExpand(s.stepId)}
                        >
                          <KeyboardArrowDown
                            sx={{
                              transform:
                                expandedStepId === s.stepId
                                  ? "rotate(180deg)"
                                  : undefined,
                              transition: "transform .2s",
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell>{fmt(s.finishedOn)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        p: 0,
                        borderBottom:
                          expandedStepId === s.stepId ? undefined : "none",
                      }}
                    >
                      <Collapse in={expandedStepId === s.stepId} unmountOnExit>
                        <StepResultDetails step={s} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  )
}
